/**
 * features/auth/auth-context.tsx — สถานะผู้ใช้ที่ล็อกอินอยู่
 *
 * ไม่เก็บ token ใน state หรือ localStorage เลย
 * token อยู่ใน cookie HttpOnly ทั้งหมด ที่เก็บใน state คือข้อมูลผู้ใช้สำหรับแสดง UI
 * และตัดสินสิทธิ์ในการแสดงเมนู (ฝั่งเซิร์ฟเวอร์ก็ตรวจสิทธิ์อีกชั้นอยู่แล้ว)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  api,
  clearStoredTokens,
  setSessionExpiredHandler,
  setStoredTokens,
} from '~/lib/api';
import type { AuthUser } from '~/lib/types';

interface AuthContextValue {
  user: AuthUser | null;
  /** true ระหว่างตรวจ session ครั้งแรก — ยังไม่รู้ว่าล็อกอินอยู่หรือไม่ */
  initializing: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  /** ตรวจว่ามี session ค้างอยู่ไหมตอนเปิดเว็บ */
  useEffect(() => {
    let cancelled = false;

    api
      .get<{ user: AuthUser }>('/api/auth/me')
      .then((response) => {
        if (!cancelled) setUser(response.user);
      })
      .catch(() => {
        // ไม่มี session — ปกติสำหรับผู้ใช้ที่ยังไม่ล็อกอิน
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** เมื่อ refresh token หมดอายุ ให้ล้าง state แล้ว router จะพาไปหน้า login เอง */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      clearStoredTokens();
      setUser(null);
    });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await api.post<{
      user: AuthUser;
      accessToken?: string;
      refreshToken?: string;
    }>('/api/auth/login', {
      username,
      password,
    });
    if (response.accessToken) {
      setStoredTokens(response.accessToken, response.refreshToken);
    }
    setUser(response.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout', {});
    } catch (error) {
      // ถ้าเรียกไม่สำเร็จก็ยังต้องล้าง state ฝั่งเบราว์เซอร์
      if (!(error instanceof ApiError)) throw error;
    } finally {
      clearStoredTokens();
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      initializing,
      login,
      logout,
      isAdmin: user?.role === 'admin',
    }),
    [user, initializing, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth ต้องอยู่ภายใน AuthProvider');
  return context;
}
